import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useEffect } from 'react';

export function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'xReplAI - Privacy Policy';
  }, []);

  return (
    <div className="min-h-screen bg-[#0A1628] dark:bg-[#0A1628]">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-24">
        <div className="prose prose-invert max-w-none">
          <Link to="/" className="text-[#60A5FA] hover:text-[#3B82F6] mb-8 inline-block">
            ← Back to Home
          </Link>
          
          <h1 className="text-4xl font-bold text-white mb-8">Privacy Policy</h1>
          <p className="text-gray-400 mb-8">Last updated: February 25, 2026</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="text-gray-300 mb-4">
              Welcome to xRepl.ai ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our Chrome extension and services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-semibold text-white mb-3">2.1 Information You Provide</h3>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li>Account information (email address, name)</li>
              <li>Email content that you choose to process with our AI</li>
              <li>User preferences and settings</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3">2.2 Automatically Collected Information</h3>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li>Usage data (features used, frequency of use)</li>
              <li>Browser type and version</li>
              <li>Technical data (IP address, device information)</li>
              <li>Error logs and performance data</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-300 mb-2">We use your information to:</p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li>Provide and improve our email drafting service</li>
              <li>Generate AI-powered email responses</li>
              <li>Customize your experience</li>
              <li>Send service-related communications</li>
              <li>Analyze usage patterns to improve our service</li>
              <li>Ensure security and prevent fraud</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">4. Data Processing and AI</h2>
            <p className="text-gray-300 mb-4">
              When you click the xReplAI button in Gmail or LinkedIn, the extension reads the current email thread or message content (subject line, message body, and sender information) and sends it to our API server for processing. <strong className="text-white">The extension never reads your emails passively or in the background</strong> — it only accesses email content when you explicitly initiate a generation.
            </p>
            <p className="text-gray-300 mb-4">
              Your email content is forwarded from our API to your selected third-party AI provider to generate draft responses. We support the following providers:
            </p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li><strong className="text-white">OpenAI</strong> (GPT-4o, GPT-4o-mini) — governed by <a href="https://openai.com/policies/privacy-policy" className="text-[#60A5FA] hover:text-[#3B82F6]" target="_blank" rel="noopener noreferrer">OpenAI's Privacy Policy</a></li>
              <li><strong className="text-white">xAI</strong> (Grok) — governed by <a href="https://x.ai/legal/privacy-policy" className="text-[#60A5FA] hover:text-[#3B82F6]" target="_blank" rel="noopener noreferrer">xAI's Privacy Policy</a></li>
              <li><strong className="text-white">Anthropic</strong> (Claude) — governed by <a href="https://www.anthropic.com/privacy" className="text-[#60A5FA] hover:text-[#3B82F6]" target="_blank" rel="noopener noreferrer">Anthropic's Privacy Policy</a></li>
            </ul>
            <p className="text-gray-300 mb-4">
              Email content is processed in real-time and is <strong className="text-white">not stored on our servers</strong> after the AI response is generated. We do not use your email content to train AI models. We retain only anonymized usage counts (number of generations, token usage) for billing and rate-limiting purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-300 mb-2">We do not sell your personal data. We may share your information with:</p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li><strong className="text-white">AI service providers</strong> (OpenAI, xAI, or Anthropic — depending on your selected model) to generate email draft responses</li>
              <li><strong className="text-white">Supabase</strong> for database hosting and authentication</li>
              <li><strong className="text-white">Vercel</strong> for API hosting and infrastructure</li>
              <li><strong className="text-white">Stripe</strong> for payment processing (we never see or store your full card details)</li>
              <li>Legal authorities when required by law</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">6. Data Security</h2>
            <p className="text-gray-300 mb-4">
              We implement industry-standard security measures to protect your data, including encryption in transit and at rest, regular security audits, and access controls. However, no method of transmission over the internet is 100% secure.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">7. Your Rights</h2>
            <p className="text-gray-300 mb-2">You have the right to:</p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to data processing</li>
              <li>Export your data</li>
              <li>Withdraw consent at any time</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">8. Cookies and Tracking</h2>
            <p className="text-gray-300 mb-4">
              Our website (xrepl.ai) uses cookies for authentication sessions and analytics. The Chrome extension itself does not use cookies — it stores user preferences (license key, selected AI model, generation settings) in Chrome's built-in extension storage. You can control cookie preferences for the website through your browser settings.
            </p>
            <p className="text-gray-300 mb-4">
              We use third-party analytics and visitor identification services to understand how visitors interact with our website:
            </p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li><strong className="text-white">Google Analytics</strong> — to analyze website traffic and usage patterns</li>
              <li><strong className="text-white">RB2B</strong> — to identify business visitors by matching IP addresses to company information. This service may identify your company name, industry, and approximate location based on your IP address. RB2B operates primarily on US-based traffic and does not collect personal information from EU visitors. For more information, see <a href="https://www.rb2b.com/privacy" className="text-[#60A5FA] hover:text-[#3B82F6]" target="_blank" rel="noopener noreferrer">RB2B's Privacy Policy</a>.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">9. Data Retention</h2>
            <p className="text-gray-300 mb-4">
              We retain your personal data only as long as necessary to provide our services and comply with legal obligations. Account data is deleted within 30 days of account closure. Usage data may be retained in anonymized form for analytics.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">10. International Data Transfers</h2>
            <p className="text-gray-300 mb-4">
              Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers in compliance with applicable data protection laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">11. Children's Privacy</h2>
            <p className="text-gray-300 mb-4">
              Our service is not intended for users under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to This Policy</h2>
            <p className="text-gray-300 mb-4">
              We may update this privacy policy from time to time. We will notify you of significant changes via email or through our service. Your continued use after such changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">13. Contact Us</h2>
            <p className="text-gray-300 mb-4">
              If you have questions about this privacy policy or our data practices, please contact us at:
            </p>
            <p className="text-gray-300">
              Email: <a href="mailto:support@xrepl.ai" className="text-[#60A5FA] hover:text-[#3B82F6]">support@xrepl.ai</a><br />
              Website: <a href="https://www.xrepl.ai" className="text-[#60A5FA] hover:text-[#3B82F6]">www.xrepl.ai</a>
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}